/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import platform from 'platform';
import getConfig from '../config';
import {getEnvironment} from '../utils/Environment';

declare let adobeIMS: AdobeIMS;

/**
 * Prints the info, log, warn and error message in the Developer Console based on environment.
 */
class LoggerUtils {
	// Exceutes the Logger Info statement
	info(componentName: string, message: string, data: any) {
		LoggerUtils._logIt('info', componentName, message, data);
	}

	// Exceutes the Logger Log statement
	log(componentName: string, message: string, data: any) {
		LoggerUtils._logIt('log', componentName, message, data);
	}

	// Exceutes the Logger Warn statement
	warn(componentName: string, message: string, data: any) {
		LoggerUtils._logIt('warn', componentName, message, data);
	}

	// Exceutes the Logger Error statement
	error(componentName: string, message: string, data: any) {
		LoggerUtils._logIt('error', componentName, message, data);
	}

	// Returns the language for the locale code
	static _getLanguageByLocale(locale) {
		const localeTolanguage = {
			en: 'English',
			ja: 'Japanese',
			de: 'German',
			fr: 'French',
			ko: 'Korean',
			it: 'Italian',
			es: 'Spanish',
			pt: 'Brazilian Portuguese',
			da: 'Danish',
			nb: 'Norwegian',
			nl: 'Dutch',
			sv: 'Swedish',
			zh_Hans: 'Chinese Simplified', // eslint-disable-line camelcase
			zh_Hant: 'Chinese Traditional (HK, TW)', // eslint-disable-line camelcase
			ru: 'Russian',
			fi: 'Finnish',
			pl: 'Polish',
		};
		let languageByLocale = 'Unknown';

		/* istanbul ignore else */
		if (localeTolanguage[locale]) {
			languageByLocale = localeTolanguage[locale];
		}

		return languageByLocale;
	}

	// Returns true if it's an Adobe user
	static _isAdobeUser() {
		let result = false;

		/* istanbul ignore else */
		if (window && window.cchome && window.cchome.util && window.cchome.util.getUserEmail()) {
			if (
				window.cchome.util.getUserEmail().split('@')[1]
				&& window.cchome.util.getUserEmail().split('@')[1] === 'adobe.com'
			) {
				result = true;
			}
		}

		return result;
	}

	// Returns true if it's an Adobe Test user
	static _isAdobeTestUser() {
		let result = false;

		/* istanbul ignore else */
		if (window && window.cchome && window.cchome.util && window.cchome.util.getUserEmail()) {
			if (
				window.cchome.util.getUserEmail().split('@')[1]
				&& window.cchome.util.getUserEmail().split('@')[1] === 'adobetest.com'
			) {
				result = true;
			}
		}

		return result;
	}

	// Returns the entitlements, fulfilledEntitlements and subscription
	static _getUserProfileInformation() {
		const userProfileInformation = {
			entitlements: [],
			fulfilledEntitlements: [],
			subscription: {},
		};

		/* istanbul ignore else */
		if (window && window.cchome && window.cchome.util && window.cchome.util.sessionStorage) {
			const cchomeProfile = window.cchome.util.sessionStorage.getItem('cchome_profile');

			if (cchomeProfile && !(cchomeProfile instanceof Error)) {
				userProfileInformation.entitlements = cchomeProfile.entitlements;
				userProfileInformation.fulfilledEntitlements = cchomeProfile.fulfilledEntitlements;
				userProfileInformation.subscription = cchomeProfile.subscription;
			}
		}

		return userProfileInformation;
	}

	// Returns the location
	static _getLocation() {
		let location = '';

		/* istanbul ignore else */
		if (window && window.cchome && window.cchome.util && window.cchome.util.geo && window.cchome.util.geo.country) {
			location = window.cchome.util.geo.country;
		}

		return location;
	}

	// Returns the screen measurement
	static _getScreenMeasurement() {
		const screenMeasurement = {
			colorDepth: 0,
			pixelDepth: 0,
			height: 0,
			width: 0,
			availHeight: 0,
			availWidth: 0,
		};

		/* istanbul ignore else */
		if (window && window.screen) {
			window.screen.colorDepth ? (screenMeasurement.colorDepth = window.screen.colorDepth) : 0;
			window.screen.pixelDepth ? (screenMeasurement.pixelDepth = window.screen.pixelDepth) : 0;
			window.screen.height ? (screenMeasurement.height = window.screen.height) : 0;
			window.screen.width ? (screenMeasurement.width = window.screen.width) : 0;
			window.screen.availHeight ? (screenMeasurement.availHeight = window.screen.availHeight) : 0;
			window.screen.availWidth ? (screenMeasurement.availWidth = window.screen.availWidth) : 0;
		}

		return screenMeasurement;
	}

	// Returns the appropriate console statement for the respective logLevel
	static _getConsoleStatement(logLevel: string, componentName: string, message: string, data: any) {
		let consoleStatement;

		switch (logLevel) {
			case 'info':
				consoleStatement = console.info(`[CCHome ${componentName}]`, message, data ? data : '');
				break;
			case 'log':
				consoleStatement = console.log(`[CCHome ${componentName}]`, message, data ? data : '');
				break;
			case 'warn':
				consoleStatement = console.warn(`[CCHome ${componentName}]`, message, data ? data : '');
				break;
			case 'error':
				consoleStatement = console.error(`[CCHome ${componentName}]`, message, data ? data : '');
				break;
			default:
				consoleStatement = console.log(`[CCHome ${componentName}]`, message, data ? data : '');
		}

		return consoleStatement;
	}

	// Executes the logging action
	static _logIt(logLevel: string, componentName: string, message: string, data: any) {
		if (message && componentName) {
			if (getEnvironment() !== 'prod' || window.cchome.util.hasFeature('enable_logger')) {
				LoggerUtils._getConsoleStatement(logLevel, componentName, message, data);
			}
			if (
				window.cchome.util.hasFeature('enable_splunk_logger')
				&& (getEnvironment() === 'stage' || getEnvironment() === 'prod')
			) {
				LoggerUtils._sendPayload(logLevel, LoggerUtils._getPayload(componentName, message, data));
			}
			else if (getEnvironment() === 'dev') {
				const hostname = location && location.hostname;
				if (hostname !== 'dev.hollywoodstudios.corp.adobe.com') {
					LoggerUtils._sendPayload(logLevel, LoggerUtils._getPayload(componentName, message, data));
				}
			}
		}
		else {
			console.error(
				`[Logger Syntax Error] Component Name or Message missing in window.cchome.util.logger.${logLevel} statement`,
			);
			console.error(`window.cchome.util.logger.${logLevel} syntax:-`);
			console.error(`window.cchome.util.logger.${logLevel}('Component Name', 'Message'`);
			console.error(`window.cchome.util.logger.${logLevel}('Component Name', 'Message', 'Data')`);
		}
	}

	/**
	 * Send Logs via network call to CCHome Support Server
	 * @param {string} level
	 * @param {Object} payload
	 */
	static _sendLogs(level: string, message: Object): Promise<any> {
		return new Promise((resolve: any, reject: any) => {
			const postData = {
				level,
				message,
			};
			const request = https.request(
				{
					hostname: getConfig('APP_SERVER'),
					path: '/v2/logs',
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'x-api-key': getConfig('CLIENT_ID'),
						Authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					},
				},
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						reject(res.statusCode);
					}
					else {
						let data = '';
						// set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', chunk => (data += chunk));
						res.on('end', () => {
							// Success!
							try {
								resolve(data);
							}
							catch (e) {
								reject(e);
							}
						});
					}
				},
			);

			request.on('error', (err) => {
				reject(err);
			});

			request.write(JSON.stringify(postData));
			request.end();
		});
	}

	// Returns the payload
	static _getPayload(payloadComponentName: string, payloadMessage: string, payloadData: any) {
		return {
			locale: LoggerUtils._getLanguageByLocale(
				window.cchome.util.getLocale() ? window.cchome.util.getLocale().split('_')[0] : '',
			),
			location: LoggerUtils._getLocation(),
			userProfileInformation: LoggerUtils._getUserProfileInformation(),
			adobeUser: LoggerUtils._isAdobeUser(),
			adobeTestUser: LoggerUtils._isAdobeTestUser(),
			browserHistoryCount: window.history ? window.history.length : 0,
			screenMeasurement: LoggerUtils._getScreenMeasurement(),
			componentName: `CCHome ${payloadComponentName}`,
			message: payloadMessage,
			data: payloadData ? payloadData : 'No data available',
			browser: {
				name: platform.name,
				description: platform.description,
				userAgent: platform.ua,
				version: platform.version,
				os: {
					architecture: platform.os.architecture,
					family: platform.os.family,
					version: platform.os.version,
				},
			},
		};
	}

	// Sends the payload to CCHome Support Server
	static async _sendPayload(level: string, payload: Object) {
		// levelToLogToServer defines the level which we want to send to the server
		const levelToLogToServer = ['info', 'warn', 'error'];
		const postData = {
			level,
			payload,
		};

		/* istanbul ignore else */
		if (levelToLogToServer.indexOf(level) !== -1) {
			try {
				/* istanbul ignore else */
				if (adobeIMS.getAccessToken() && typeof adobeIMS.getAccessToken() === 'string') {
					await LoggerUtils._sendLogs(level, payload);
					if (window.cchome.util.hasFeature('enable_logger_debug')) {
						console.log('CCHome Support Server Logger Payload:', postData);
					}
				}
			}
			catch (error) {
				if (window.cchome.util.hasFeature('enable_logger_debug')) {
					console.error('CCHome Support Server error sending Payload', postData, error);
				}
			}
		}
	}
}

export default LoggerUtils;
